// Visit Note sub-module CATALOGUE.
//
// Deliberately import-free so it can be unit-tested under node:test — the rest
// of `useEncounterTabConfig` pulls in React and the PowerSync web SDK, neither
// of which loads outside a browser. Re-exported from `useEncounterTabConfig`,
// so existing import sites are unchanged.

// ─── Types ───

export interface TabConfig {
  key: string;
  label: string;
  visible: boolean;
}

// ─── Default tabs ───

export const DEFAULT_TABS: TabConfig[] = [
  { key: 'summary', label: 'Summary', visible: true },
  // PRD.233 — Chief Complaint promoted to a first-class section sitting
  // immediately after Summary. Preference parsing will insert this for
  // existing users whose stored config predates this entry.
  { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
  // PRD.310 — generic, encounter-scoped Medical History narrative. Catalogue
  // default is OFF: only disciplines that ask for it (Dental, via
  // GUIDED_PRESET_DEFAULT_ON in data-access) switch it on by default, so no
  // other clinic type's preset changes. Users can enable it anywhere.
  { key: 'medical_history', label: 'Medical History', visible: false },
  // PRD.314 — generic Specialty History sub-modules. Catalogue default OFF;
  // the Dental / Aesthetic guided presets switch their own one on, so no other
  // clinic type's preset changes. Stored clinical identity stays 'dental' /
  // 'derma' — these are only UI keys.
  { key: 'dental_history', label: 'Dental History', visible: false },
  { key: 'derma_history', label: 'Derma History', visible: false },
  // PRD.316 — Clinical Findings. Generic encounter documentation with OPTIONAL
  // dental context, so it is catalogue-default OFF and the Dental preset turns
  // its own copy on; any discipline can enable it manually.
  { key: 'clinical_findings', label: 'Clinical Findings', visible: false },
  { key: 'vitals', label: 'Vitals', visible: true },
  { key: 'charting', label: 'Charting', visible: true },
  { key: 'notes', label: 'Notes', visible: true },
  // PRD.214 — Symptoms & Diagnosis structured chip entry
  { key: 'symptoms', label: 'Symptoms', visible: true },
  { key: 'diagnoses', label: 'Diagnosis', visible: true },
  { key: 'prescriptions', label: 'Prescriptions', visible: true },
  // PRD.305 — Labs sits with the other ordering sub-modules, right after
  // Prescriptions: both are things a clinician orders off the back of the
  // diagnosis.
  { key: 'labs', label: 'Labs', visible: true },
  { key: 'treatment_plan', label: 'Treatment Plan', visible: true },
  // PRD.318 — the DENTAL treatment workflow (plan + performed records). A
  // separate key from `treatment_plan` so the generic section keeps its meaning
  // for other specialties; catalogue default OFF, the Dental preset turns it on.
  { key: 'dental_treatment_plan', label: 'Dental Treatment Plan', visible: false },
  // PRD.319 — dental diagnostic/imaging documentation (OPG, IOPA, CBCT…).
  // Distinct from Laboratory, which models orders and results. Catalogue
  // default OFF; the Dental preset turns it on.
  { key: 'investigations', label: 'Investigations', visible: false },
  { key: 'forms', label: 'Forms', visible: true },
  { key: 'files', label: 'Files', visible: true },
  { key: 'invoices', label: 'Invoices', visible: true },
  // PRD.201 — Encounter Pharmacy tab. Sits between Invoices and Payments
  // because dispense eligibility is driven by paid invoice lines.
  { key: 'pharmacy', label: 'Pharmacy', visible: true },
  { key: 'payments', label: 'Payments', visible: true },
];

/**
 * Sub-modules kept in the codebase but suppressed everywhere in the UI — the
 * tab strip, the accordion, the guided cards AND the settings drawer's toggle
 * list (they flow through the same availability set as clinic/RBAC gating).
 *
 * This is a deliberate temporary hide, not a deletion: the catalogue entry, the
 * preset entries and the section renderer all stay, and saved preferences keep
 * their stored visibility for these keys. Removing a key from this set restores
 * it exactly as it was.
 */
export const HIDDEN_SUBMODULE_KEYS: ReadonlySet<string> = new Set([
  // Treatment Plan — hidden pending its rework. `TreatmentPlanSection` and the
  // `treatment_plan` cards in every guided preset remain wired up.
  'treatment_plan',
]);
